/** @file pivot2: where the drawn bone leaves the tile, on half pixels, inset by cutOff (design 21.6 step 1). */
import { TILE_EDGES, type TileEdge } from '@zilo/pose3d-render-core/cut/tileEdges';

/** The second pivot in image px and the tile edge it sits on. */
export type Pivot2 = { point: [number, number]; edge: TileEdge };

const EPS = 1e-9;
/** Pivots may sit half a pixel past an edge (`halfPixel` on a right-edge pivot, 18.7). */
const SLACK = 0.5;

/** Ray parameter to the edge `p + t d` leaves `[0, size]` through on one axis; infinity along the other axis. */
const edgeT = (d: number, p: number, size: number): number => (d > EPS ? (size - p) / d : d < -EPS ? -p / d : Infinity);

/** Rounded to half pixels, halves toward +infinity. */
const half = (v: number): number => Math.floor(v * 2 + 0.5) / 2;

/**
 * Follows the ray from `pivot` along `u` (image px, y down) to the first tile edge (top/bottom on a corner tie); the
 * exit point's coordinate along that edge is rounded to half pixels, then moved `cutOff` px inward, perpendicular to
 * the edge. A pivot more than half a pixel outside the tile, or already on its exit edge, is its own pivot2.
 */
export function pivot2Of(pivot: [number, number], u: [number, number], texW: number, texH: number, cutOff: number): Pivot2 {
  const tx = edgeT(u[0], pivot[0], texW);
  const ty = edgeT(u[1], pivot[1], texH);
  const edge: TileEdge = ty <= tx ? (u[1] > 0 ? 'bottom' : 'top') : (u[0] > 0 ? 'right' : 'left');
  const outside = pivot[0] < -SLACK || pivot[0] > texW + SLACK || pivot[1] < -SLACK || pivot[1] > texH + SLACK;
  const t = Math.max(0, Math.min(tx, ty));
  if (outside || !Number.isFinite(t) || t === 0) return { point: [pivot[0], pivot[1]], edge };
  const e = TILE_EDGES[edge];
  const across = (e.far ? (e.axis === 0 ? texW : texH) : 0) + (e.far ? -cutOff : cutOff);
  const along = e.axis === 0 ? half(pivot[1] + t * u[1]) : half(pivot[0] + t * u[0]);
  return { point: e.axis === 0 ? [across, along] : [along, across], edge };
}
